'use client';

import { useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Button } from '@/components/ui/forms/button';
import { Plus, X, Trash2 } from 'lucide-react';
import { generateId } from '../../utils';


/**
 * Skill keyword chip.
 *
 * Deliberately not `Badge`: that is the status-pill treatment (10px, uppercase,
 * wide tracking) and it shouts. A skill is user-authored content, so it keeps the
 * casing the user typed and sits on the same 12px body scale as the inputs above
 * it — a keyword, not a state label.
 */
function SkillChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-border bg-muted/30 px-2 py-1 text-body-sm text-foreground">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

export default function SkillsSection() {
  const { resume, updateContent, addSkillCategory, updateSkillCategory, removeSkillCategory } =
    useResumeBuilderStore();
  const [technicalInput, setTechnicalInput] = useState('');
  const [softInput, setSoftInput] = useState('');
  const [categoryInputs, setCategoryInputs] = useState<Record<string, string>>({});

  if (!resume) return null;

  const { skills } = resume.content;

  const handleAddTechnical = () => {
    if (!technicalInput.trim()) return;
    updateContent({
      skills: {
        ...skills,
        technical: [...skills.technical, technicalInput.trim()],
      },
    });
    setTechnicalInput('');
  };

  const handleRemoveTechnical = (skill: string) => {
    updateContent({
      skills: {
        ...skills,
        technical: skills.technical.filter((s) => s !== skill),
      },
    });
  };

  const handleAddSoft = () => {
    if (!softInput.trim()) return;
    updateContent({
      skills: {
        ...skills,
        soft: [...skills.soft, softInput.trim()],
      },
    });
    setSoftInput('');
  };

  const handleRemoveSoft = (skill: string) => {
    updateContent({
      skills: {
        ...skills,
        soft: skills.soft.filter((s) => s !== skill),
      },
    });
  };

  const handleAddCategory = () => {
    addSkillCategory({
      id: generateId(),
      name: 'New Category',
      skills: [],
    });
  };

  const handleAddCategorySkill = (categoryId: string) => {
    const input = categoryInputs[categoryId];
    if (!input?.trim()) return;

    const category = skills.categories.find((c) => c.id === categoryId);
    if (category) {
      updateSkillCategory(categoryId, {
        skills: [...category.skills, input.trim()],
      });
    }
    setCategoryInputs((prev) => ({ ...prev, [categoryId]: '' }));
  };

  const handleRemoveCategorySkill = (categoryId: string, skill: string) => {
    const category = skills.categories.find((c) => c.id === categoryId);
    if (category) {
      updateSkillCategory(categoryId, {
        skills: category.skills.filter((s) => s !== skill),
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Technical Skills */}
      <div className="space-y-2">
        <Label>Technical Skills</Label>
        <div className="flex flex-wrap gap-2">
          {skills.technical.map((skill) => (
            <SkillChip key={skill} label={skill} onRemove={() => handleRemoveTechnical(skill)} />
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={technicalInput}
            onChange={(e) => setTechnicalInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTechnical())}
            placeholder="Add a skill..."
            className="flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={handleAddTechnical}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Soft Skills */}
      <div className="space-y-2">
        <Label>Soft Skills</Label>
        <div className="flex flex-wrap gap-2">
          {skills.soft.map((skill) => (
            <SkillChip key={skill} label={skill} onRemove={() => handleRemoveSoft(skill)} />
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={softInput}
            onChange={(e) => setSoftInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSoft())}
            placeholder="Add a skill..."
            className="flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={handleAddSoft}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Custom Categories */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Custom Skill Categories</Label>
          <Button type="button" variant="outline" size="sm" onClick={handleAddCategory}>
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        </div>

        {skills.categories.map((category) => (
          <div key={category.id} className="rounded-lg border p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={category.name}
                onChange={(e) => updateSkillCategory(category.id, { name: e.target.value })}
                placeholder="Category name"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeSkillCategory(category.id)}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {category.skills.map((skill) => (
                <SkillChip
                  key={skill}
                  label={skill}
                  onRemove={() => handleRemoveCategorySkill(category.id, skill)}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={categoryInputs[category.id] || ''}
                onChange={(e) =>
                  setCategoryInputs((prev) => ({ ...prev, [category.id]: e.target.value }))
                }
                onKeyDown={(e) =>
                  e.key === 'Enter' && (e.preventDefault(), handleAddCategorySkill(category.id))
                }
                placeholder="Add a skill..."
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddCategorySkill(category.id)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
